import { genXAPIValidData } from '@vedalib/courseAnalitycs'
import { useInViewport } from 'ahooks'
import { useCallback, useRef } from 'react'

import { sendCourseAnalytics } from 'services/Store/Project/actions'
import { PreviewMode } from 'services/Store/Project/enums'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import {
  getCourseState,
  getBlock,
  getProgress,
  getProject,
  getSectionId,
} from 'services/Store/Project/selectors'
import type { IBlockMode } from 'services/Store/Project/types'

import { FEATURE_FLAGS } from '../config'
import { LmsService } from '../lms.service'
import { useEntityViewTracking } from './useEntityViewTracking'

interface IUseBlockViewed {
  blockId: string
  mode: IBlockMode
  ref?: React.RefObject<HTMLDivElement>
}

export const useBlockViewed = ({ blockId, mode, ref }: IUseBlockViewed) => {
  const [inViewPort] = useInViewport(ref)
  const project = useProjectContext(getProject)
  const dispatch = useProjectDispatch()
  const sectionId = useProjectContext(getSectionId)
  const fromBlockId = useProjectContext(getBlock, { prev: true })?.uuid
  const progress = useProjectContext(getProgress) ?? 0

  const courseFinished = useProjectContext(getCourseState).status === 'finished'
  const viewedBlock = mode.previewMode === PreviewMode.scorm && FEATURE_FLAGS.viewedBlock
  const needViewed = Boolean(inViewPort !== undefined || viewedBlock || blockId)
  const projectId = project?.id || ''
  const companyId = project?.companyId || ''

  const payloadRef = useRef({ progress, fromBlockId, sectionId, companyId, projectId })
  payloadRef.current = { progress, fromBlockId, sectionId, companyId, projectId }

  const buildBlockData = useCallback(
    (elementType: 'enteredBlock' | 'exitedBlock') => {
      const { progress, fromBlockId, sectionId, companyId, projectId } = payloadRef.current
      return genXAPIValidData({
        elementType,
        verb: elementType,
        data: {
          resultExtensions: { courseId: projectId, blockId, fromBlockId },
          objectExtensions: {
            blockId,
            courseId: projectId,
            companyId,
            projectId,
            sectionId,
            progress,
          },
        },
      }).xAPIData
    },
    [blockId],
  )

  const setViewed = useCallback(
    () => dispatch(sendCourseAnalytics(buildBlockData('enteredBlock'))),
    [dispatch, buildBlockData],
  )
  const setUnViewed = useCallback(
    () => dispatch(sendCourseAnalytics(buildBlockData('exitedBlock'))),
    [dispatch, buildBlockData],
  )
  const setUnViewedWaiting = useCallback(
    () => LmsService.instance.sendAnalyticsViaBeacon([buildBlockData('exitedBlock')]),
    [buildBlockData],
  )

  useEntityViewTracking({
    inViewPort,
    setViewed,
    setUnViewed,
    setUnViewedWaiting,
    needViewed,
    exitObserve: inViewPort,
    courseFinished,
  })
}
